// Actions
import { ActionType } from "@ms/uno-actions/lib/local/ActionType";
// Constants
import { PlannerServiceLimitErrorTypes } from "@ms/uno-constants/lib/local/ErrorConstants";
import { HttpStatus } from "@ms/uno-constants/lib/local/HttpStatusConstants";
import { ModalType } from "@ms/uno-constants/lib/local/ModalType";
import { BasicPlan, DefaultPlanId, FlaggedEmailPlanId, StoreEntityStatus, StoreTableNames } from "@ms/uno-constants/lib/local/StoreConstants";
import { TraceLevel } from "@ms/uno-telemetry/lib/local/events/Trace.event";
// Entity
import { ExistenceStatus } from "@ms/uno-entitycore/lib/local/EntityStatus";
import { BasicTable } from "@ms/uno-entitycore/lib/local/tables/BasicTable";
import { EntityTable } from "@ms/uno-entitycore/lib/local/tables/EntityTable";
import { PlanEntitySetBuilder } from "@ms/uno-models/lib/local/client/legacyPlanner/plan/PlanEntitySetBuilder";
import { PlanMoveOperationStatus } from "@ms/uno-models/lib/local/client/legacyPlanner/plan/PlanMoveOperationStatus";
import { IUpdateEntityStatusForICalendar } from "@ms/uno-models/lib/local/client/modal/ICalendarModalData";
import { PlanDetailsSerializationHandler } from "@ms/uno-models/lib/local/client/persistence/legacyPlans/PlanDetailsSerializationHandler";
import { PlanSerializationHandler } from "@ms/uno-models/lib/local/client/persistence/legacyPlans/PlanSerializationHandler";
import { StoreEntityStatusSerializationHandler } from "@ms/uno-models/lib/local/client/persistence/legacyStatus/StoreEntityStatusSerializationHandler";
import { ContainerType } from "@ms/uno-models/lib/local/service/graph-legacy/container/ContainerType";
// Stores
import { EventPublishingStore } from "@ms/uno-fluxcore/lib/local/stores/EventPublishingStore";
import isEqual from "lodash/isEqual";
import { logUnreachableAction } from "../../../utilities/LoggingUtilities";
import { getStatusBasedOnResponse } from "../../../utilities/StatusUtilities";
import { isPersistedStorageEnabled, upsertVersionedBaseline } from "../../../utilities/StoreUtilities";
export class BasicPlanStore extends EventPublishingStore {
    /**
     * Hydrates the store from the persistent storage. For storing the default plan id and flagged email plan id.
     */ hydrateFromPersistentStorage() {
        this.defaultPlanId = this.persistentStorage?.getItem(DefaultPlanId) ?? null;
        this.flaggedEmailPlanId = this.persistentStorage?.getItem(FlaggedEmailPlanId) ?? null;
    }
    get name() {
        return "BasicPlanStore";
    }
    getPlan(id, readBaseline) {
        const plan = this.planTable.get(id);
        const status = this.planStatusTable.get(id) ?? StoreEntityStatus.Unfetched;
        const isFetching = this.isPlanFetchingInProgress[id] ?? false;
        return {
            data: (readBaseline ? plan?.baseline : plan?.value) ?? null,
            status,
            isFetching
        };
    }
    getPlanDetails(id, readBaseline) {
        const planDetails = this.planDetailsTable.get(id);
        return (readBaseline ? planDetails?.baseline : planDetails?.value) ?? null;
    }
    getPlansForContainerFetchFailureStatus(containerType, externalId) {
        return this.containerFailureStatusTable.get(containerType)?.[externalId] ?? null;
    }
    getPlansForContainer(containerType, externalId) {
        const index = this.planTable.getByIndex({
            name: containerType,
            value: externalId
        });
        const status = this.containerStatusTable.get(containerType)?.[externalId] ?? StoreEntityStatus.Unfetched;
        const plans = [];
        for (const id of Object.keys(index)){
            const plan = this.planTable.get(id);
            if (!plan || plan.status === ExistenceStatus.SoftDeleted) {
                continue;
            }
            const planDetails = this.planDetailsTable.get(id);
            plans.push(new PlanEntitySetBuilder().withPlan(plan.value).withDetails(planDetails?.value).build());
        }
        return {
            data: plans,
            status
        };
    }
    getAllPlans() {
        const allPlans = this.planTable.getByIndex({
            name: "allBasicPlans",
            value: BasicPlan
        });
        const planArray = [];
        for (const key of Object.keys(allPlans)){
            planArray.push(allPlans[key].value);
        }
        return planArray;
    }
    getPlansForContainerType(containerType, hideArchivedPlans) {
        if (containerType === ContainerType.ToDo && !this.configProvider().settings.EnvironmentFeatureSettings.EnableTodoExperience) {
            return {
                data: [],
                status: StoreEntityStatus.UnableToFetch
            };
        }
        const allPlans = this.planTable.getByIndex({
            name: "containerType",
            value: containerType
        });
        const status = this.plansInContainerStatusTable.get(containerType) ?? StoreEntityStatus.Unfetched;
        const planArray = [];
        for (const key of Object.keys(allPlans)){
            if (hideArchivedPlans && allPlans[key].value.isArchived) {
                continue;
            }
            planArray.push(new PlanEntitySetBuilder().withPlan(allPlans[key].value).withDetails(this.planDetailsTable.get(key)?.value).build());
        }
        return {
            data: planArray,
            status
        };
    }
    getDefaultPlanId() {
        return this.defaultPlanId;
    }
    getFlaggedEmailPlanId() {
        return this.flaggedEmailPlanId;
    }
    isUserView(planId) {
        return planId === this.getDefaultPlanId() || planId === this.getFlaggedEmailPlanId();
    }
    getPlansForContainerTypeIsLastPageFetchedStatus(containerType) {
        return !!this.plansInContainerLastPageStatusTable.get(containerType);
    }
    getUpdatePlanStatusForICalendar() {
        return this.updatePlanStatusForICalendar;
    }
    getBasicPlanMoveOperationStatus(planId) {
        return this.moveBasicPlanOperationStatusTable.get(planId);
    }
    getPersistedTables() {
        return {
            [StoreTableNames.Plans]: this.planTable,
            [StoreTableNames.PlanDetails]: this.planDetailsTable,
            [StoreTableNames.PlanStatus]: this.planStatusTable
        };
    }
    handleAction(action) {
        let storeChanged = false;
        switch(action.type){
            case ActionType.FetchBasicPlanAction:
                storeChanged = this.handleFetchBasicPlanAction(action);
                break;
            case ActionType.FetchBasicPlanFailedAction:
                storeChanged = this.handleFetchBasicPlanFailedAction(action);
                break;
            case ActionType.FetchBasicPlanSucceededAction:
                storeChanged = this.handleFetchBasicPlanSucceededAction(action);
                break;
            case ActionType.FetchPlansForChannelGroupIdAction:
                storeChanged = this.handleFetchPlansForChannelGroupIdAction(action);
                break;
            case ActionType.FetchPlansForChannelGroupIdFailedAction:
                storeChanged = this.handleFetchPlansForChannelGroupIdFailedAction(action);
                break;
            case ActionType.FetchPlansForChannelGroupIdSucceededAction:
                storeChanged = this.handleFetchPlansForChannelGroupIdSucceededAction(action);
                break;
            case ActionType.FetchBasicPlansForContainerAction:
                storeChanged = this.handleFetchBasicPlansForContainerAction(action);
                break;
            case ActionType.FetchBasicPlansForContainerFailedAction:
                storeChanged = this.handleFetchBasicPlansForContainerFailedAction(action);
                break;
            case ActionType.FetchBasicPlansForContainerSucceededAction:
                storeChanged = this.handleFetchBasicPlansForContainerSucceededAction(action);
                break;
            case ActionType.FetchAllBasicPlansForContainerTypeAction:
                storeChanged = this.handleFetchAllBasicPlansForContainerTypeAction(action);
                break;
            case ActionType.FetchAllBasicPlansForContainerTypeFailedAction:
                storeChanged = this.handleFetchAllBasicPlansForContainerTypeFailedAction(action);
                break;
            case ActionType.FetchAllBasicPlansForContainerTypeSucceededAction:
                storeChanged = this.handleFetchAllBasicPlansForContainerTypeSucceededAction(action);
                break;
            case ActionType.CreateBasicPlanAction:
                storeChanged = this.handleCreateBasicPlanAction(action);
                break;
            case ActionType.CreateBasicPlanFailedAction:
                storeChanged = this.handleCreateBasicPlanFailedAction(action);
                break;
            case ActionType.CreateBasicPlanSucceededAction:
                storeChanged = this.handleCreateBasicPlanSucceededAction(action);
                break;
            case ActionType.DeleteBasicPlanAction:
                storeChanged = this.handleDeleteBasicPlanAction(action);
                break;
            case ActionType.DeleteBasicPlanFailedAction:
                storeChanged = this.handleDeleteBasicPlanFailedAction(action);
                break;
            case ActionType.DeleteBasicPlanSucceededAction:
                storeChanged = this.handleDeleteBasicPlanSucceededAction(action);
                break;
            case ActionType.DeltaSyncDeleteBasicPlanAction:
                storeChanged = this.handleDeltaSyncDeleteBasicPlanAction(action);
                break;
            case ActionType.UpdateBasicPlanAction:
                storeChanged = this.handleUpdateBasicPlanAction(action);
                break;
            case ActionType.UpdateBasicPlanFailedAction:
                storeChanged = this.handleUpdateBasicPlanFailedAction(action);
                break;
            case ActionType.UpdateBasicPlanSucceededAction:
                storeChanged = this.handleUpdateBasicPlanSucceededAction(action);
                break;
            case ActionType.DeltaSyncUpdateBasicPlanAction:
                storeChanged = this.handleDeltaSyncUpdateBasicPlanAction(action);
                break;
            case ActionType.SetDefaultPlanAction:
                storeChanged = this.handleSetDefaultPlanAction(action);
                break;
            case ActionType.SetFlaggedEmailPlanAction:
                storeChanged = this.handleSetFlaggedEmailPlanAction(action);
                break;
            case ActionType.CloseModalAction:
                if (action.modalData.modalType === ModalType.ICalendarDialog) {
                    this.updatePlanStatusForICalendar = IUpdateEntityStatusForICalendar.None;
                    storeChanged = true;
                }
                break;
            case ActionType.MoveBasicPlanOperationAction:
                storeChanged = this.handleMoveBasicPlanOperationAction(action);
                break;
            case ActionType.MoveBasicPlanOperationCompletedAction:
                storeChanged = this.handleMoveBasicPlanOperationCompletedAction(action);
                break;
            case ActionType.MoveBasicPlanOperationCompletedWithErrorsAction:
                storeChanged = this.handleMoveBasicPlanOperationCompletedWithErrorsAction(action);
                break;
            default:
                logUnreachableAction(this, this.loggers, action);
                return;
        }
        if (storeChanged) {
            this.publishStoreChangedEvent();
        }
    }
    handleFetchBasicPlanAction(action) {
        const { planId } = action;
        if (this.isPlanFetchingInProgress[planId]) {
            return false;
        }
        this.isPlanFetchingInProgress[planId] = true;
        return true;
    }
    handleFetchBasicPlanFailedAction(action) {
        let storeChanged = false;
        const { response, planId } = action;
        if (this.isPlanFetchingInProgress[planId]) {
            this.isPlanFetchingInProgress[planId] = false;
            storeChanged = true;
        }
        // Update the status if appropriate
        if (response?.status === HttpStatus.NotFound || response?.status === HttpStatus.Forbidden) {
            storeChanged = this.updatePlanStatusBasedOnResponse(planId, response) || storeChanged;
        } else {
            this.planStatusTable.set(planId, StoreEntityStatus.UnableToFetch);
            storeChanged = true;
        }
        return storeChanged;
    }
    handleFetchBasicPlanSucceededAction(action) {
        const { plan, planDetails } = action.planEntitySet;
        return this.upsertPlanEntitySet(plan, planDetails);
    }
    handleFetchPlansForChannelGroupIdAction(action) {
        const { groupId } = action;
        const groupStatus = this.containerStatusTable.get(ContainerType.Group) ?? {};
        if (groupStatus[groupId] === StoreEntityStatus.Fetching) {
            return false;
        }
        groupStatus[groupId] = StoreEntityStatus.Fetching;
        this.containerStatusTable.set(ContainerType.Group, groupStatus);
        return true;
    }
    handleFetchPlansForChannelGroupIdFailedAction(action) {
        const { groupId, response } = action;
        let storeChanged = false;
        const groupStatus = this.containerStatusTable.get(ContainerType.Group) ?? {};
        // Update the status if appropriate
        if (groupStatus[groupId] !== StoreEntityStatus.UnableToFetch) {
            groupStatus[groupId] = StoreEntityStatus.UnableToFetch;
            this.containerStatusTable.set(ContainerType.Group, groupStatus);
            storeChanged = true;
        }
        const groupFailureStatus = this.containerFailureStatusTable.get(ContainerType.Group) ?? {};
        if (groupFailureStatus[groupId] !== response?.status && response) {
            groupFailureStatus[groupId] = response?.status;
            this.containerFailureStatusTable.set(ContainerType.Group, groupFailureStatus);
            storeChanged = true;
        }
        return storeChanged;
    }
    handleFetchPlansForChannelGroupIdSucceededAction(action) {
        const { planEntitySets } = action;
        let storeChanged = false;
        for (const planEntitySet of planEntitySets){
            const { plan, planDetails } = planEntitySet;
            storeChanged = this.upsertPlanEntitySet(plan, planDetails) || storeChanged;
            const groupStatus = this.containerStatusTable.get(ContainerType.Group) ?? {};
            if (groupStatus[plan.container.externalId] !== StoreEntityStatus.Present) {
                groupStatus[plan.container.externalId] = StoreEntityStatus.Present;
                this.containerStatusTable.set(ContainerType.Group, groupStatus);
                storeChanged = true;
            }
        }
        return storeChanged;
    }
    handleFetchBasicPlansForContainerAction(action) {
        const { containerType, containerExternalId } = action;
        const containerStatus = this.containerStatusTable.get(containerType) ?? {};
        if (containerStatus[containerExternalId] === StoreEntityStatus.Fetching) {
            return false;
        }
        containerStatus[containerExternalId] = StoreEntityStatus.Fetching;
        this.containerStatusTable.set(containerType, containerStatus);
        return true;
    }
    handleFetchBasicPlansForContainerFailedAction(action) {
        const { response, containerExternalId, containerType } = action;
        const containerStatus = this.containerStatusTable.get(containerType) ?? {};
        // If no response or response is not 404 or 403, set status to UnableToFetch
        if (!response || response.status !== HttpStatus.NotFound && response.status !== HttpStatus.Forbidden) {
            containerStatus[containerExternalId] = StoreEntityStatus.UnableToFetch;
            this.containerStatusTable.set(containerType, containerStatus);
            return true;
        }
        if (response.status === HttpStatus.NotFound && containerStatus[containerExternalId] !== StoreEntityStatus.NotFound) {
            containerStatus[containerExternalId] = StoreEntityStatus.NotFound;
            this.containerStatusTable.set(containerType, containerStatus);
            return true;
        }
        if (response.status === HttpStatus.Forbidden && containerStatus[containerExternalId] !== StoreEntityStatus.Inaccessible) {
            containerStatus[containerExternalId] = StoreEntityStatus.Inaccessible;
            this.containerStatusTable.set(containerType, containerStatus);
            return true;
        }
        return false;
    }
    handleFetchBasicPlansForContainerSucceededAction(action) {
        const { containerType, containerExternalId, planEntitySets } = action;
        let storeChanged = false;
        for (const planEntitySet of planEntitySets){
            const plan = planEntitySet.plan;
            storeChanged = this.upsertPlanEntitySet(plan, planEntitySet.planDetails) || storeChanged;
        }
        // Update the index status if there are any changes
        const containerStatus = this.containerStatusTable.get(containerType) ?? {};
        if (containerStatus[containerExternalId] !== StoreEntityStatus.Present) {
            containerStatus[containerExternalId] = StoreEntityStatus.Present;
            this.containerStatusTable.set(containerType, containerStatus);
            storeChanged = true;
        }
        return storeChanged;
    }
    /**
     * Handles the fetch all basic plans for container type action.
     * @param action FetchAllBasicPlansForContainerTypeAction to handle.
     */ handleFetchAllBasicPlansForContainerTypeAction(action) {
        return this.plansInContainerStatusTable.set(action.containerType, StoreEntityStatus.Fetching);
    }
    /**
     * Handles the fetch all basic plans for container type failed action.
     * @param action FetchAllBasicPlansForContainerTypeFailedAction to handle.
     */ handleFetchAllBasicPlansForContainerTypeFailedAction(action) {
        let storeChanged = false;
        const { containerType, response, isLastPage } = action;
        const status = response != null ? getStatusBasedOnResponse(response) : StoreEntityStatus.UnableToFetch;
        if (status) {
            storeChanged = this.plansInContainerStatusTable.set(containerType, status) || storeChanged;
        }
        storeChanged = this.plansInContainerLastPageStatusTable.set(containerType, isLastPage) || storeChanged;
        return storeChanged;
    }
    /**
     * Handles the fetch all basic plans for container type succeeded action.
     * @param action FetchAllBasicPlansForContainerTypeSucceededAction to handle.
     */ handleFetchAllBasicPlansForContainerTypeSucceededAction(action) {
        const { containerType, isLastPage } = action;
        let storeChanged = false;
        storeChanged = this.plansInContainerStatusTable.set(containerType, StoreEntityStatus.Present) || storeChanged;
        storeChanged = this.plansInContainerLastPageStatusTable.set(containerType, isLastPage) || storeChanged;
        return storeChanged;
    }
    handleCreateBasicPlanAction(action) {
        const { planEntitySet } = action;
        const { plan, planDetails } = planEntitySet;
        // Add to the store tables
        this.addPlanAndStatus(plan);
        // Process the plan details
        if (planDetails != null) {
            // Add to the store tables
            this.planDetailsTable.add(planDetails.id, planDetails);
        }
        return true;
    }
    handleCreateBasicPlanFailedAction(action) {
        const { planEntitySet, response } = action;
        const { plan } = planEntitySet;
        // Validate the plan exists in the store. We will assume this can adequately be used to determine if the plan details exists in the store as well.
        const storeCopy = this.planTable.get(plan.id);
        if (!storeCopy) {
            /**
             * This can happen if a local delete or delta sync delete occurs before
             * the response of the create returns. Log a warning and no-op.
             */ this.loggers.traceLogger.logTrace(0x1e2495d3 /* tag_4jjxt */ , TraceLevel.Warning, `PlanNotFoundInStore [Action=${action.type}][Id=${plan.id}]`);
            return false;
        }
        // Remove from the store tables
        this.deletePlanAndStatus(storeCopy.baseline.id);
        this.planDetailsTable.delete(plan.id);
        // Update the status if appropriate
        if (response != null) {
            this.updatePlanStatusBasedOnResponse(plan.id, response);
        }
        return true;
    }
    handleCreateBasicPlanSucceededAction(action) {
        const { planEntitySet, localId } = action;
        const { plan, planDetails } = planEntitySet;
        const storeCopy = this.planTable.get(localId);
        if (!storeCopy) {
            /**
             * This can happen if a local delete or delta sync delete occurs before
             * the response of the create returns. Log a warning and no-op.
             */ this.loggers.traceLogger.logTrace(0x1e2495d2 /* tag_4jjxs */ , TraceLevel.Warning, `PlanNotFoundInStore [Action=${action.type}][Id=${plan.id}]`);
            return false;
        }
        const storeDetailsCopy = this.planDetailsTable.get(localId);
        if (!storeDetailsCopy) {
            this.loggers.traceLogger.logTrace(0x1e2495d1 /* tag_4jjxr */ , TraceLevel.Warning, `PlanDetailsNotFoundInStore [Action=${action.type}][Id=${plan.id}]`);
            return false;
        }
        // Update the store tables with the server copy of the plan
        storeCopy.updateBaseline(plan);
        this.planTable.reKey(localId, plan.id);
        this.planStatusTable.set(plan.id, StoreEntityStatus.Present);
        if (storeDetailsCopy != null && planDetails != null) {
            storeDetailsCopy.updateBaseline(planDetails);
            this.planDetailsTable.reKey(localId, plan.id);
        }
        // Remove the local copy from the store tables
        this.deletePlanAndStatus(localId);
        this.planDetailsTable.delete(localId);
        return true;
    }
    handleDeleteBasicPlanAction(action) {
        const { planId } = action;
        let storeChanged = false;
        const storeCopy = this.planTable.get(planId);
        if (!storeCopy) {
            this.loggers.traceLogger.logTrace(0x1e2495d0 /* tag_4jjxq */ , TraceLevel.Warning, `PlanNotFoundInStore [Action=${action.type}][Id=${planId}]`);
            return false;
        }
        // No need to delete if already soft deleted.
        if (storeCopy.status === ExistenceStatus.SoftDeleted) {
            this.loggers.traceLogger.logTrace(0x1e2495cf /* tag_4jjxp */ , TraceLevel.Warning, `StoreException: Basic plan already soft deleted in store [PlanId=${planId}]`);
            return storeChanged;
        }
        // Soft delete the plan
        storeCopy.softDelete();
        storeChanged = true;
        const storeDetailsCopy = this.planDetailsTable.get(planId);
        if (storeDetailsCopy != null) {
            storeDetailsCopy.softDelete();
            storeChanged = true;
        }
        this.planStatusTable.set(planId, StoreEntityStatus.SoftDeleted);
        return storeChanged;
    }
    handleDeleteBasicPlanFailedAction(action) {
        const { planId, response } = action;
        let storeChanged = false;
        const storeCopy = this.planTable.get(planId);
        const storeDetailsCopy = this.planDetailsTable.get(planId);
        if (!storeCopy) {
            /**
             * This can happen if a delta sync delete occurs before the
             * response of the create returns. Log a warning and no-op.
             */ this.loggers.traceLogger.logTrace(0x1e2495ce /* tag_4jjxo */ , TraceLevel.Warning, `PlanNotFoundInStore [Action=${action.type}][Id=${planId}]`);
            return false;
        }
        if (storeCopy.status === ExistenceStatus.SoftDeleted) {
            // Un-delete the plan
            storeCopy.undelete();
            if (storeDetailsCopy != null) {
                storeDetailsCopy.undelete();
            }
            this.planStatusTable.set(planId, StoreEntityStatus.Present);
            storeChanged = true;
        } else {
            this.loggers.traceLogger.logTrace(0x1e2495cd /* tag_4jjxn */ , TraceLevel.Warning, `StoreException: Basic plan deletion failure for non soft-deleted plan [PlanId=${planId}]`);
        }
        // Update the status if appropriate
        if (response != null) {
            storeChanged = this.updatePlanStatusBasedOnResponse(planId, response) || storeChanged;
        }
        return storeChanged;
    }
    handleDeleteBasicPlanSucceededAction(action) {
        const { planId } = action;
        const storeCopy = this.planTable.get(planId);
        if (storeCopy == null) {
            // Log this case. This can happen in the rare chance if a delta sync returns before the response of the delete
            const msg = `PlanNotFoundInStore [PlanId=${planId}]`;
            this.loggers.traceLogger.logTrace(0x1e2495cc /* tag_4jjxm */ , TraceLevel.Info, msg);
            return false;
        }
        // Remove item from the store
        this.deletePlanAndStatus(planId);
        this.planDetailsTable.delete(planId);
        return true;
    }
    handleDeltaSyncDeleteBasicPlanAction(action) {
        const { planId } = action;
        const storeCopy = this.planTable.get(planId);
        if (storeCopy == null) {
            // This can happen in the case where a local delete occurs before a delta sync based delete.
            // Log this case with a warning because the feed processor should be ignoring this delta.
            this.loggers.traceLogger.logTrace(0x1e2495cb /* tag_4jjxl */ , TraceLevel.Warning, `PlanNotFoundInStore [PlanId=${planId}]`);
            return false;
        }
        // Hard delete the plan
        this.deletePlanAndStatus(planId);
        this.planDetailsTable.delete(planId);
        return true;
    }
    handleUpdateBasicPlanAction(action) {
        let storeChanged = false;
        const { planId, planDelta, planDetailsDelta, updateKey } = action;
        const storeCopy = this.planTable.get(planId);
        const storeDetailsCopy = this.planDetailsTable.get(planId);
        if (!storeCopy) {
            this.loggers.traceLogger.logTrace(0x1e2495ca /* tag_4jjxk */ , TraceLevel.Warning, `PlanNotFoundInStore [Action=${action.type}][Id=${planId}]`);
            return false;
        }
        if (storeCopy.status === ExistenceStatus.SoftDeleted) {
            this.loggers.traceLogger.logTrace(0x1e2495c9 /* tag_4jjxj */ , TraceLevel.Warning, `StoreException: Basic plan soft deleted in store [PlanId=${planId}]`);
            return false;
        }
        // Handle plan delta
        if (planDelta != null) {
            const updatedPlan = storeCopy.value.getClone().applyDiffs(planDelta);
            if (!isEqual(storeCopy.value, updatedPlan)) {
                this.loggers.traceLogger.logTrace(0x1e2495c8 /* tag_4jjxi */ , TraceLevel.Verbose, `Pushing delta for Basic plan [PlanId=${planId}][UpdateKey=${updateKey}][DeltaCount=${storeCopy.deltaCount}]`);
                storeCopy.pushDelta(planDelta, updateKey);
                storeChanged = true;
            }
        }
        // Handle plan details delta
        if (storeDetailsCopy != null && planDetailsDelta != null) {
            const updatedPlanDetails = storeDetailsCopy.value.getClone().applyDiffs(planDetailsDelta);
            if (!isEqual(storeDetailsCopy.value, updatedPlanDetails)) {
                this.loggers.traceLogger.logTrace(0x1e2495c7 /* tag_4jjxh */ , TraceLevel.Verbose, `Pushing delta for Basic plan details [PlanId=${planId}][UpdateKey=${updateKey}][DeltaCount=${storeDetailsCopy.deltaCount}]`);
                storeDetailsCopy.pushDelta(planDetailsDelta, updateKey);
                storeChanged = true;
            }
            if (planDetailsDelta.iCalendarPublishEnabled) {
                this.updatePlanStatusForICalendar = IUpdateEntityStatusForICalendar.PublishingTasks;
            } else if (planDetailsDelta.iCalendarPublishEnabled === false) {
                this.updatePlanStatusForICalendar = IUpdateEntityStatusForICalendar.UnpublishingTasks;
            }
        }
        return storeChanged;
    }
    handleUpdateBasicPlanFailedAction(action) {
        let storeChanged = false;
        const { planId, response, updateKey, errorType } = action;
        const storeCopy = this.planTable.get(planId);
        const storeDetailsCopy = this.planDetailsTable.get(planId);
        if (storeCopy == null || storeCopy.status === ExistenceStatus.SoftDeleted) {
            // This can happen if a local delete or delta sync delete occurs before the response of the update returns. Log a warning and no-op
            this.loggers.traceLogger.logTrace(0x1e2495c6 /* tag_4jjxg */ , TraceLevel.Warning, `PlanNotFoundInStore [PlanId=${planId}][ExistenceStatus=${storeCopy?.status ?? "Non-existent"}]`);
            return false;
        }
        // Revert the plan delta if it exists
        if (storeCopy.getDelta(updateKey) != null) {
            storeCopy.revertDelta(updateKey);
            storeChanged = true;
        }
        // Revert the plan details delta if it exists
        if (storeDetailsCopy?.getDelta(updateKey) != null) {
            storeDetailsCopy.revertDelta(updateKey);
            storeChanged = true;
        }
        // Update the status if necessary
        if (response != null) {
            storeChanged = this.updatePlanStatusBasedOnResponse(planId, response, errorType) || storeChanged;
        }
        if (this.updatePlanStatusForICalendar === IUpdateEntityStatusForICalendar.PublishingTasks) {
            this.updatePlanStatusForICalendar = IUpdateEntityStatusForICalendar.PublishFailed;
            storeChanged = true;
        } else if (this.updatePlanStatusForICalendar === IUpdateEntityStatusForICalendar.UnpublishingTasks) {
            this.updatePlanStatusForICalendar = IUpdateEntityStatusForICalendar.UnpublishFailed;
            storeChanged = true;
        }
        return storeChanged;
    }
    handleUpdateBasicPlanSucceededAction(action) {
        let storeChanged = false;
        const { planId, updatedPlan, updatedPlanDetails, updateKey } = action;
        const storeCopy = this.planTable.get(planId);
        const storeDetailsCopy = this.planDetailsTable.get(planId);
        if (storeCopy == null || storeCopy.status === ExistenceStatus.SoftDeleted) {
            // This can happen if a local delete or delta sync delete occurs before the response of the update returns. Log a warning and no-op
            this.loggers.traceLogger.logTrace(0x1e2495c5 /* tag_4jjxf */ , TraceLevel.Warning, `PlanNotFoundInStore [PlanId=${planId}][ExistenceStatus=${storeCopy?.status ?? "Non-existent"}]`);
            return false;
        }
        // Commit the pending plan update
        if (updatedPlan != null && storeCopy.getDelta(updateKey) != null) {
            storeCopy.commitDelta(updateKey, updatedPlan);
            storeChanged = true;
        }
        // Commit the pending plan details update
        if (updatedPlanDetails != null && storeDetailsCopy != null && storeDetailsCopy.getDelta(updateKey) != null) {
            storeDetailsCopy.commitDelta(updateKey, updatedPlanDetails);
            storeChanged = true;
        }
        if (this.updatePlanStatusForICalendar === IUpdateEntityStatusForICalendar.PublishingTasks) {
            this.updatePlanStatusForICalendar = IUpdateEntityStatusForICalendar.PublishSucceeded;
            storeChanged = true;
        } else if (this.updatePlanStatusForICalendar === IUpdateEntityStatusForICalendar.UnpublishingTasks) {
            this.updatePlanStatusForICalendar = IUpdateEntityStatusForICalendar.UnpublishSucceeded;
            storeChanged = true;
        }
        return storeChanged;
    }
    handleDeltaSyncUpdateBasicPlanAction(action) {
        let storeChanged = false;
        const { planId, updatedPlan, updatedPlanDetails } = action;
        const storeCopy = this.planTable.get(planId);
        const storeDetailsCopy = this.planDetailsTable.get(planId);
        if (!storeCopy) {
            this.loggers.traceLogger.logTrace(0x1e2495c4 /* tag_4jjxe */ , TraceLevel.Warning, `PlanNotFoundInStore [Action=${action.type}][Id=${planId}]`);
            return false;
        }
        // Handle plan update
        if (updatedPlan != null) {
            storeChanged = this.upsertPlanEntitySet(updatedPlan, updatedPlanDetails, false) || storeChanged;
            if (storeCopy.status === ExistenceStatus.SoftDeleted) {
                // This is an indicator that the out going delete request will fail from a delete conflict. Log a warning and only update the baseline if needed
                this.loggers.traceLogger.logTrace(0x1e2495c3 /* tag_4jjxd */ , TraceLevel.Warning, `StoreException: Basic plan soft deleted in store [PlanId=${planId}]`);
            }
        }
        // Handle plan details update
        if (updatedPlanDetails != null && storeDetailsCopy != null) {
            storeChanged = upsertVersionedBaseline(this.planDetailsTable, planId, updatedPlanDetails) || storeChanged;
            if (storeDetailsCopy.status === ExistenceStatus.SoftDeleted) {
                // This is an indicator that the out going delete request will fail from a delete conflict. Log a warning and only update the baseline if needed
                this.loggers.traceLogger.logTrace(0x1e2495c2 /* tag_4jjxc */ , TraceLevel.Warning, `StoreException: Basic plan details soft deleted in store [PlanId=${planId}]`);
            }
        }
        return storeChanged;
    }
    handleMoveBasicPlanOperationAction(action) {
        return this.moveBasicPlanOperationStatusTable.set(action.planId, PlanMoveOperationStatus.InProgress);
    }
    handleMoveBasicPlanOperationCompletedAction(action) {
        return this.moveBasicPlanOperationStatusTable.set(action.planId, PlanMoveOperationStatus.Completed);
    }
    handleMoveBasicPlanOperationCompletedWithErrorsAction(action) {
        return this.moveBasicPlanOperationStatusTable.set(action.planId, PlanMoveOperationStatus.CompletedWithErrors);
    }
    handleSetDefaultPlanAction(action) {
        // Do not set the default plan Id if Todo is not enabled in the environment
        if (this.defaultPlanId === action.value || !this.configProvider().settings.EnvironmentFeatureSettings.EnableTodoExperience) {
            return false;
        }
        this.defaultPlanId = action.value;
        if (isPersistedStorageEnabled(this.configProvider, this.persistentStorage)) {
            this.persistentStorage?.setItem(DefaultPlanId, this.defaultPlanId);
        }
        return true;
    }
    handleSetFlaggedEmailPlanAction(action) {
        // Do not set the flagged email plan id if Todo is not enabled in the environment
        if (this.flaggedEmailPlanId === action.value || !this.configProvider().settings.EnvironmentFeatureSettings.EnableTodoExperience) {
            return false;
        }
        this.flaggedEmailPlanId = action.value;
        if (isPersistedStorageEnabled(this.configProvider, this.persistentStorage)) {
            this.persistentStorage?.setItem(FlaggedEmailPlanId, this.flaggedEmailPlanId);
        }
        return true;
    }
    /**
     * Add a plan to the store tables
     * @param plan Basic plan to add
     */ addPlanAndStatus(plan) {
        this.planTable.add(plan.id, plan);
        this.planStatusTable.set(plan.id, StoreEntityStatus.Present);
    }
    /**
     * Delete a plan from the store tables
     * @param planId Id of the basic plan
     */ deletePlanAndStatus(planId) {
        this.planTable.delete(planId);
        this.planStatusTable.delete(planId);
    }
    /**
     * Upsert a plan entity set into the store
     * @param plan Plan
     * @param planDetails Plan details
     * @param markPresent Whether to mark the plan as present
     */ upsertPlanEntitySet(plan, planDetails, markPresent = true) {
        let storeChanged = false;
        if (this.isPlanFetchingInProgress[plan.id]) {
            this.isPlanFetchingInProgress[plan.id] = false;
            storeChanged = true;
        }
        // Process the plan
        storeChanged = upsertVersionedBaseline(this.planTable, plan.id, plan) || storeChanged;
        // Process the plan details
        if (planDetails) {
            storeChanged = upsertVersionedBaseline(this.planDetailsTable, planDetails.id, planDetails) || storeChanged;
        }
        if (markPresent) {
            // Update the status if appropriate
            storeChanged = this.planStatusTable.set(plan.id, StoreEntityStatus.Present) || storeChanged;
        }
        return storeChanged;
    }
    /**
     * Update the status of a plan based on the request response
     * @param planId Id of the basic plan
     * @param response Response from the request
     * @param errorType Error type
     * @summary If the plan hits a limit error, the response from Graph will be 403 Forbidden. We do not want to set the status to Inaccessible in this case.
     */ updatePlanStatusBasedOnResponse(planId, response, errorType = "") {
        const status = getStatusBasedOnResponse(response);
        const isLimitsError = errorType === PlannerServiceLimitErrorTypes.MaximumProjectsSharedWithUser || errorType === PlannerServiceLimitErrorTypes.MaximumUsersSharedWithProject;
        if (status === StoreEntityStatus.Inaccessible && isLimitsError) {
            // If the plan is inaccessible and the plan reached a limit error we need to do nothing to the status
            return false;
        } else if (status) {
            return this.planStatusTable.set(planId, status);
        }
        return false;
    }
    constructor(configProvider, loggers, persistentStorage){
        super(), this.configProvider = configProvider, this.loggers = loggers, this.persistentStorage = persistentStorage, this.planTable = new EntityTable(StoreTableNames.Plans, {
            containerType: (plan)=>plan.container.containerType,
            group: (plan)=>plan.container.containerType === ContainerType.Group ? plan.container.externalId : undefined,
            user: (plan)=>plan.container.containerType === ContainerType.User ? plan.container.externalId : undefined,
            roster: (plan)=>plan.container.containerType === ContainerType.Roster ? plan.container.externalId : undefined,
            driveItem: (plan)=>plan.container.containerType === ContainerType.DriveItem ? plan.container.externalId : undefined,
            project: (plan)=>plan.container.containerType === ContainerType.Project ? plan.container.externalId : undefined,
            teamsChannel: (plan)=>plan.container.containerType === ContainerType.TeamsChannel ? plan.container.externalId : undefined,
            todo: (plan)=>plan.container.containerType === ContainerType.ToDo ? plan.container.externalId : undefined,
            allBasicPlans: (plan)=>plan.container.containerType !== ContainerType.ToDo && plan.container.containerType !== ContainerType.Project ? BasicPlan : undefined,
            plannerTask: (plan)=>plan.container.containerType === ContainerType.PlannerTask ? plan.container.externalId : undefined
        }, new PlanSerializationHandler()), this.planDetailsTable = new EntityTable(StoreTableNames.PlanDetails, {}, new PlanDetailsSerializationHandler()), this.defaultPlanId = null, this.flaggedEmailPlanId = null, this.planStatusTable = new BasicTable(StoreTableNames.PlanStatus, {}, new StoreEntityStatusSerializationHandler()), this.moveBasicPlanOperationStatusTable = new BasicTable(StoreTableNames.MoveBasicPlanOperationStatus), this.containerStatusTable = new BasicTable(StoreTableNames.ContainerStatus), this.containerFailureStatusTable = new BasicTable(StoreTableNames.ContainerFailureStatus), this.plansInContainerStatusTable = new BasicTable(StoreTableNames.PlansInContainerStatus), this.plansInContainerLastPageStatusTable = new BasicTable(StoreTableNames.PlansInContainerLastPageStatus), this.updatePlanStatusForICalendar = IUpdateEntityStatusForICalendar.None, this.isPlanFetchingInProgress = {};
        if (isPersistedStorageEnabled(this.configProvider, this.persistentStorage) && this.configProvider().settings.EnvironmentFeatureSettings.EnableTodoExperience) {
            this.hydrateFromPersistentStorage();
        }
    }
}
